/*
 *  -----------------------------------------------------------------------
 *    <copyright file="import-study-config/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogContent } from '@mui/material';
import cn from 'clsx';
import React, { useEffect, useMemo, useState } from 'react';
import {
    formISCData,
    generateFileDetails,
    getISCColumns,
    getISCNextBtnText,
    getISCSubtitles,
    getISCTitles,
} from '@components/admin/import-study-config/import-study-config.helper';
import {
    ImportStudyConfigProps,
    ISCDialogPages,
    StudyConfigImportExportStatus,
} from '@components/admin/import-study-config/import-study-config.interface';
import LibraryGrid from '@components/admin/library-grid';
import FileUploader from '@components/core/file-uploader';
import Loading from '@components/core/loading';
import StepsBox from '@components/core/steps-box';
import TextField from '@components/core/text-field';
import { useTranslation } from 'hooks';
import Button from 'views/components/core/buttons';
import { CustomDialog, DialogActions, DialogTitle } from 'views/components/core/dialog';
import './styles.scss';
import { stringDateWithoutTimeFromCurrentUtcDate } from '@utils/convertor';
import { MODAL_CLOSE_ACTIONS } from '@utils/modal-helper';
import {
    studyConfigImport,
    studyConfigImport_OpraMutations_studyConfigImport_ConfigImportResults as ConfigImportResults,
    studyConfigImportVariables,
} from '@queries/generated/studies.generated';
import { STUDY_CONFIG_IMPORT } from '@queries/studies';

const ImportStudyConfig = ({ onClose, studyId }: ImportStudyConfigProps) => {
    const { t } = useTranslation();

    const [page, setPage] = useState<{ number: ISCDialogPages; params: string; available: ISCDialogPages }>({
        number: 1,
        params: '',
        available: 1,
    });
    const [file, setFile] = useState<any>(null);
    const [fileError, setFileError] = useState<string>();
    const [importSummaryData, setImportSummaryData] = useState<ConfigImportResults | null>(null);
    const [importData, setImportData] = useState<ConfigImportResults | null>(null);

    // Disable Graphql/apollo caching because when there are ~1000 messages it goes really slow trying to cache the values and the values don't need to be cached.
    const [importConfig, { loading }] = useMutation<studyConfigImport, studyConfigImportVariables>(
        STUDY_CONFIG_IMPORT,
        { fetchPolicy: 'no-cache' },
    );

    const pageTitles = getISCTitles(t);
    const pageSubtitles = getISCSubtitles(t);
    const isStepTwo = page.number === 2;
    const isStepThree = page.number === 3;
    const isStepFour = page.number === 4;
    const backBtnText = t(isStepTwo ? 'admin:fileUpload' : 'admin:fileDetails');
    const nextBtnText = getISCNextBtnText(t);
    const showGrid = isStepThree || (isStepFour && !loading);
    const showBackBtn = isStepTwo || isStepThree;
    const gridData = isStepThree ? importSummaryData?.itemResults : importData?.itemResults;
    const excelExportFileName = `${file?.StudyConfig?.Study?.ShortName}_${stringDateWithoutTimeFromCurrentUtcDate(
        'DD-MMM-YYYY',
    )}.opra.xlsx`;
    const exportConfig = { fileName: excelExportFileName };

    const sortedGridData = useMemo(() => {
        const priorityStatus = StudyConfigImportExportStatus.Failure;

        return (gridData ?? [])
            .filter((a) => !!a)
            .sort((a, b) => {
                if (a.status === priorityStatus && b.status !== priorityStatus) return -1;
                if (a.status !== priorityStatus && b.status === priorityStatus) return 1;
                return 0;
            });
    }, [gridData]);

    useEffect(() => {
        if (importSummaryData || importData) {
            setImportSummaryData(null);
            setImportData(null);
        }
    }, [file]);

    const updateStep = (current: ISCDialogPages, available?: ISCDialogPages) => {
        setPage({ number: current, params: '', available: available || page.available });
    };

    const nextBtnClick = (nextPageNumber: ISCDialogPages) => {
        switch (page.number) {
            case 1:
                if (file) {
                    updateStep(nextPageNumber, (page.number + 1) as ISCDialogPages);
                } else {
                    setFileError(t<string>('admin:invalidJsonFile'));
                }
                break;
            case 2:
                if (!importSummaryData) {
                    importConfig({
                        variables: {
                            contents: JSON.stringify(file),
                            studyId,
                            targetConfigHash: null,
                        },
                    })
                        .then((res) => {
                            if (res.data?.studyConfigImport) {
                                setImportSummaryData(res.data.studyConfigImport);
                            }
                        })
                        .catch(() => {
                            onClose(MODAL_CLOSE_ACTIONS.ERROR);
                        });
                }

                updateStep(nextPageNumber, (page.number + 1) as ISCDialogPages);
                break;
            case 3:
                if (!importData) {
                    importConfig({
                        variables: {
                            contents: JSON.stringify(file),
                            studyId,
                            targetConfigHash: importSummaryData?.targetConfigHash,
                            observationCount: importSummaryData?.observationCount,
                        },
                    })
                        .then((res) => {
                            if (res.data?.studyConfigImport) {
                                setImportData(res.data.studyConfigImport);
                            }
                        })
                        .catch(() => {
                            onClose(MODAL_CLOSE_ACTIONS.ERROR);
                        });
                }

                updateStep(nextPageNumber, (page.number + 1) as ISCDialogPages);
                break;
            case 4:
                onClose();
                break;
            default:
                updateStep(nextPageNumber, (page.number + 1) as ISCDialogPages);
                break;
        }
    };

    const backBtnClick = (pageNumber: ISCDialogPages) => {
        updateStep(pageNumber);
    };

    const onChangeStep = (pageNumber: number) => {
        if (file) {
            updateStep(pageNumber as ISCDialogPages);
        }
    };

    const onFileUpload = (newFile: any) => {
        if (newFile && newFile?.StudyConfig?.Study) {
            setFile(newFile);
            updateStep(2, 2);
        } else {
            setFileError(t<string>('admin:invalidJsonFile'));
        }
    };

    return (
        <CustomDialog
            onClose={() => onClose()}
            fullWidth
            maxWidth="md"
            scroll="paper"
            open={open}
            className="custom-width-popup import-study-config max-w700"
        >
            <DialogTitle id="import-study-config-modal-title">
                {t('admin:updateStudy')}
                <FontAwesomeIcon icon={['fal', 'times']} className="fa-lg close-icon-cv" onClick={() => onClose()} />
            </DialogTitle>

            <DialogContent className="import-study-config__content">
                <StepsBox page={page} onChangeStep={onChangeStep} pageTitles={pageTitles} />

                {loading ? (
                    <Loading />
                ) : (
                    <>
                        <div className="import-study-config__subtitle">{pageSubtitles[page.number]}</div>

                        <div className={cn('file-uploader-wrapper', { hidden: page.number !== 1 })}>
                            <FileUploader
                                acceptedFileTypes={{
                                    'application/octet-stream': ['.opra'],
                                }}
                                onFileUpload={onFileUpload}
                                error={fileError}
                                onError={(error) => setFileError(error ?? undefined)}
                                showFileName
                            />
                        </div>

                        {isStepTwo && (
                            <TextField
                                id="import-file-data"
                                multiline
                                rows={20}
                                fullWidth
                                defaultValue={generateFileDetails(file, t)}
                                variant="outlined"
                                color="secondary"
                                className="mb non-disabled"
                                disabled
                            />
                        )}
                        {showGrid && (
                            <div className="mb-20">
                                <LibraryGrid
                                    items={formISCData(sortedGridData)}
                                    columns={getISCColumns(t, isStepFour)}
                                    exportConfig={exportConfig}
                                />
                            </div>
                        )}
                    </>
                )}

                <DialogActions className={cn('import-study-config__actions', { 'flex-end': page.number < 2 })}>
                    {showBackBtn && (
                        <Button
                            text={backBtnText}
                            id="back-button"
                            disabled={false}
                            onClick={() => backBtnClick((page.number - 1) as ISCDialogPages)}
                        />
                    )}
                    {page.number <= 4 && (
                        <Button
                            id="next-step-btn"
                            className={cn({ 'ml-auto': !(isStepTwo || isStepThree) })}
                            text={nextBtnText[page.number]}
                            disabled={!!fileError}
                            onClick={() => nextBtnClick((page.number + 1) as ISCDialogPages)}
                        />
                    )}
                </DialogActions>
            </DialogContent>
        </CustomDialog>
    );
};

export default ImportStudyConfig;
